import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import ScrollToTop from "react-scroll-to-top";
import { fetchProducts, searchProducts } from "../api/productsApi";
import { fetchVouchers } from "../api/voucherApi";
import BannerOne from "../components/BannerOne";
import BottomFooter from "../components/BottomFooter";
import HeaderOne from "../components/HeaderOne";
import ProductListOne from "../components/ProductListOne";
import ColorInit from "../helper/ColorInit";
import Preloader from "../helper/Preloader";

function shuffleArray(array) {
	const shuffled = [...array];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled;
}

const HomePageOne = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [category, setCategory] = useState("all");
	const [searchedProducts, setSearchedProducts] = useState(null);
	const [isSearching, setIsSearching] = useState(false);

	// Fetch all products and vouchers initially
	const {
		data: products = [],
		isLoading: productsLoading,
		isError: productsIsError,
		error: productsError,
		refetch: refetchProducts,
	} = useQuery({
		queryKey: ["products"],
		queryFn: fetchProducts,
		staleTime: 1000 * 60 * 5,
	});

	const {
		data: vouchers = [],
		isLoading: vouchersLoading,
		isError: vouchersIsError,
		error: vouchersError,
		refetch: refetchVouchers,
	} = useQuery({
		queryKey: ["vouchers"],
		queryFn: fetchVouchers,
		staleTime: 1000 * 60 * 5,
	});

	// 🧩 Handle dynamic product search
	useEffect(() => {
		setIsSearching(true);

		const handleSearch = async () => {
			if (category === "products" && searchTerm.trim()) {
				try {
					const results = await searchProducts(searchTerm);
					setSearchedProducts(results);
				} catch (error) {
					console.error("Product search failed:", error);
					setSearchedProducts([]);
				} finally {
					setIsSearching(false);
				}
			} else {
				setSearchedProducts(null);
				setIsSearching(false);
			}
		};

		const delayDebounce = setTimeout(() => handleSearch(), 400); // Debounce 400ms
		return () => clearTimeout(delayDebounce);
	}, [searchTerm, category]);

	// 🧩 Combine data initially (for “All” view)
	const combinedItems = useMemo(() => {
		const productList = products.map((p) => ({ ...p, isVoucher: false }));
		const voucherList = vouchers.map((v) => ({
			id: v.id,
			name: v.name,
			images: [{ src: v.image_url }],
			price: v.denominations?.[0]?.min || 0,
			details: v.details,
			currency: v.currency,
			deliveryType: v.delivery_type,
			isVoucher: true,
		}));
		return shuffleArray([...productList, ...voucherList]);
	}, [products, vouchers]);

	// 🧩 Filter logic
	// const filteredItems = useMemo(() => {
	// 	if (category === "vouchers") {
	// 		let list = vouchers.map((v) => ({
	// 			id: v.id,
	// 			name: v.name,
	// 			images: [{ src: v.image_url }],
	// 			price: v.denominations?.[0]?.min || 0,
	// 			details: v.details,
	// 			currency: v.currency,
	// 			deliveryType: v.delivery_type,
	// 			isVoucher: true,
	// 		}));
	// 		if (searchTerm.trim()) {
	// 			const term = searchTerm.toLowerCase();
	// 			list = list.filter((v) => v.name.toLowerCase().includes(term));
	// 		}
	// 		return list;
	// 	}

	// 	if (category === "products" && searchedProducts) {
	// 		return searchedProducts.map((p) => ({ ...p, isVoucher: false }));
	// 	}

	// 	// Default: All combined
	// 	let list = combinedItems;
	// 	if (searchTerm.trim()) {
	// 		const term = searchTerm.toLowerCase();
	// 		list = list.filter((item) =>
	// 			item.name?.toLowerCase().includes(term)
	// 		);
	// 	}
	// 	return list;
	// }, [category, searchTerm, vouchers, combinedItems, searchedProducts]);

	const filteredItems = useMemo(() => {
		if (!vouchers?.length && !products?.length) return [];

		const mapVouchers = (list) =>
			list.map((v) => ({
				id: v.id,
				name: v.name,
				images: [{ src: v.image_url }],
				price: v.denominations?.[0]?.min || 0,
				details: v.details,
				currency: v.currency,
				deliveryType: v.delivery_type,
				isVoucher: true,
			}));

		const mapProducts = (list) =>
			list.map((p) => ({ ...p, isVoucher: false }));

		const term = searchTerm.toLowerCase().trim();
		const isSearching = term.length > 0;

		// ✅ use full lists when searching (no slicing)
		if (isSearching) {
			if (category === "vouchers") {
				return mapVouchers(vouchers).filter((v) =>
					v.name.toLowerCase().includes(term)
				);
			}

			if (category === "products" && searchedProducts) {
				return mapProducts(searchedProducts).filter((p) =>
					p.name.toLowerCase().includes(term)
				);
			}

			return shuffleArray([
				...mapProducts(products).filter((p) =>
					p.name?.toLowerCase().includes(term)
				),
				...mapVouchers(vouchers).filter((v) =>
					v.name?.toLowerCase().includes(term)
				),
			]);
		}

		// ✅ not searching → show limited vouchers
		if (category === "vouchers") {
			return mapVouchers(vouchers).slice(0, 14);
		}

		if (category === "products") {
			return mapProducts(products);
		}

		// ✅ “All” category → 14 vouchers + all products
		return shuffleArray([
			...mapProducts(products),
			...mapVouchers(vouchers).slice(0, 14),
		]);
	}, [
		category,
		searchTerm,
		vouchers?.length,
		products?.length,
		searchedProducts,
	]);

	// const filteredItems = useMemo(() => {
	// 	const mapVouchers = (list) =>
	// 		list.map((v) => ({
	// 			id: v.id,
	// 			name: v.name,
	// 			images: [{ src: v.image_url }],
	// 			price: v.denominations?.[0]?.min || 0,
	// 			details: v.details,
	// 			currency: v.currency,
	// 			deliveryType: v.delivery_type,
	// 			isVoucher: true,
	// 		}));

	// 	const mapProducts = (list) =>
	// 		list.map((p) => ({ ...p, isVoucher: false }));

	// 	const isSearching = searchTerm.trim().length > 0;
	// 	const term = searchTerm.toLowerCase();

	// 	// 1️⃣ Searching mode → full list (no slicing)
	// 	if (isSearching) {
	// 		if (category === "vouchers") {
	// 			return mapVouchers(vouchers).filter((v) =>
	// 				v.name.toLowerCase().includes(term)
	// 			);
	// 		}

	// 		if (category === "products" && searchedProducts) {
	// 			return mapProducts(searchedProducts).filter((p) =>
	// 				p.name.toLowerCase().includes(term)
	// 			);
	// 		}

	// 		// Default search: all products + all vouchers
	// 		return shuffleArray([
	// 			...mapProducts(products),
	// 			...mapVouchers(vouchers).filter((v) =>
	// 				v.name.toLowerCase().includes(term)
	// 			),
	// 		]);
	// 	}

	// 	// 2️⃣ Not searching → limit vouchers to 14 only
	// 	if (category === "vouchers") {
	// 		return mapVouchers(vouchers).slice(0, 14);
	// 	}

	// 	if (category === "products") {
	// 		return mapProducts(products);
	// 	}

	// 	// Combined "All" → products + only 14 vouchers
	// 	return shuffleArray([
	// 		...mapProducts(products),
	// 		...mapVouchers(vouchers).slice(0, 14),
	// 	]);
	// }, [category, searchTerm, vouchers, products, searchedProducts]);

	// 🧩 Handle error state
	if (productsLoading || vouchersLoading) return <Preloader />;
	if (productsIsError && vouchersIsError) {
		return (
			<div className="container d-flex flex-column justify-content-center align-items-center vh-100 text-center">
				<div
					className="card shadow border-0 p-5"
					style={{ maxWidth: "480px" }}
				>
					<div className="card-body">
						<h2 className="card-title text-danger mb-3">
							<i className="bi bi-exclamation-triangle-fill me-2"></i>
							Failed to Load Data
						</h2>
						<p className="text-muted mb-4">
							We couldn’t load products and vouchers. Please try
							again later.
						</p>
						<div className="alert alert-danger text-start">
							<strong>Products:</strong>{" "}
							{productsError?.message || "Unknown error"}
							<br />
							<strong>Vouchers:</strong>{" "}
							{vouchersError?.message || "Unknown error"}
						</div>
						<button
							onClick={() => {
								refetchProducts();
								refetchVouchers();
							}}
							className="btn btn-success btn-lg mt-3 px-4"
						>
							<i className="bi bi-arrow-clockwise me-2"></i>
							Retry
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<ScrollToTop smooth color="#299E60" />
			<ColorInit color={false} />

			<HeaderOne
				searchTerm={searchTerm}
				onSearchChange={setSearchTerm}
				category={category}
				onCategoryChange={setCategory}
			/>

			{/* Hide banner when searching */}
			{!searchTerm.trim() && <BannerOne />}

			<ProductListOne
				products={filteredItems}
				isLoading={productsLoading || vouchersLoading || isSearching}
				isError={productsIsError && vouchersIsError}
			/>

			<BottomFooter />
		</>
	);
};

export default HomePageOne;

// import React from "react";
// import Preloader from "../helper/Preloader";
// import HeaderOne from "../components/HeaderOne";
// import BannerOne from "../components/BannerOne";
// import FeatureOne from "../components/FeatureOne";
// import PromotionalOne from "../components/PromotionalOne";
// import FlashSalesOne from "../components/FlashSalesOne";
// import ProductListOne from "../components/ProductListOne";
// import OfferOne from "../components/OfferOne";
// import RecommendedOne from "../components/RecommendedOne";
// import HotDealsOne from "../components/HotDealsOne";
// import TopVendorsOne from "../components/TopVendorsOne";
// import BestSellsOne from "../components/BestSellsOne";
// import DeliveryOne from "../components/DeliveryOne";
// import OrganicOne from "../components/OrganicOne";
// import ShortProductOne from "../components/ShortProductOne";
// import BrandOne from "../components/BrandOne";
// import NewArrivalOne from "../components/NewArrivalOne";
// import ShippingOne from "../components/ShippingOne";
// import NewsletterOne from "../components/NewsletterOne";
// import FooterOne from "../components/FooterOne";
// import BottomFooter from "../components/BottomFooter";
// import ScrollToTop from "react-scroll-to-top";
// import ColorInit from "../helper/ColorInit";
// const HomePageOne = () => {

//   return (

// 	<>

// 	  {/* Preloader */}
// 	  <Preloader />

// 	  {/* ScrollToTop */}
// 	  <ScrollToTop smooth color="#299E60" />

// 	  {/* ColorInit */}
// 	  <ColorInit color={false} />

// 	  {/* HeaderOne */}
// 	  <HeaderOne />

// 	  {/* BannerOne */}
// 	  <BannerOne />

// 	  {/* FeatureOne */}
// 	  <FeatureOne />

// 	  {/* PromotionalOne */}
// 	  <PromotionalOne />

// 	  {/* FlashSalesOne */}
// 	  <FlashSalesOne />

// 	  {/* ProductListOne */}
// 	  <ProductListOne />

// 	  {/* OfferOne */}
// 	  <OfferOne />

// 	  {/* RecommendedOne */}
// 	  <RecommendedOne />

// 	  {/* HotDealsOne */}
// 	  <HotDealsOne />

// 	  {/* TopVendorsOne */}
// 	  <TopVendorsOne />

// 	  {/* BestSellsOne */}
// 	  <BestSellsOne />

// 	  {/* DeliveryOne */}
// 	  <DeliveryOne />

// 	  {/* OrganicOne */}
// 	  <OrganicOne />

// 	  {/* ShortProductOne */}
// 	  <ShortProductOne />

// 	  {/* BrandOne */}
// 	  <BrandOne />

// 	  {/* NewArrivalOne */}
// 	  <NewArrivalOne />

// 	  {/* ShippingOne */}
// 	  <ShippingOne />

// 	  {/* NewsletterOne */}
// 	  <NewsletterOne />

// 	  {/* FooterOne */}
// 	  <FooterOne />

// 	  {/* BottomFooter */}
// 	  <BottomFooter />

// 	</>
//   );
// };

// export default HomePageOne;
